import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
  LogWeightRequestDto,
  ProfileDto,
  UpdateCoachPersonaDto,
  UpdateWeightRequestDto,
  WeightEntryDto,
  WeightPointDto,
} from '../types';

export function useRecentWeight(days = 90) {
  return useQuery({
    queryKey: ['weight', 'recent', days],
    queryFn: ({ signal }) =>
      api.get<WeightEntryDto[]>(`/api/weight/recent?days=${days}`, { signal }),
    select: (rows) =>
      [...rows].sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()),
  });
}

// Trend series for the chart. Decoupled from useRecentWeight so a long-range chart query
// (e.g. "last year", "all") doesn't disturb the dense recent data powering the hero/list.
// The server downsamples long spans to weekly averages.
export function useWeightSeries(from: string, to: string) {
  return useQuery({
    queryKey: ['weight', 'series', from, to],
    queryFn: ({ signal }) =>
      api.get<WeightPointDto[]>(
        `/api/weight/series?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        { signal },
      ),
  });
}

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: async ({ signal }) => {
      const resp = await fetch('/api/profile', { credentials: 'include', signal });
      if (resp.status === 404) return null;
      if (!resp.ok) throw new Error(`/api/profile failed: ${resp.status}`);
      return (await resp.json()) as ProfileDto;
    },
    retry: false,
  });
}

export function useLogWeight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LogWeightRequestDto) => api.post<WeightEntryDto>('/api/weight', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['weight', 'recent'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useUpdateWeight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateWeightRequestDto }) =>
      api.put<WeightEntryDto>(`/api/weight/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['weight', 'recent'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useUpdateCoachPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCoachPersonaDto) => api.put<void>('/api/profile/coach-persona', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['coaching', 'today'] });
      void qc.invalidateQueries({ queryKey: ['coaching', 'plan'] });
    },
  });
}

export function useDeleteWeight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/weight/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['weight', 'recent'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useRestoreWeight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/weight/${id}/restore`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['weight', 'recent'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}
